import { createClient, AuthChangeEvent, Session, User, SupabaseClient, AuthError, Subscription, Provider } from '@supabase/supabase-js';
import type { Database } from './database.types';
import { create } from 'zustand';
import { useCollectionStore } from './collectionStore';

// Get environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Constants for retry logic
const MAX_AUTH_RETRIES = 3;
const AUTH_RETRY_DELAY = 2000;

// Helper function to check if Supabase is configured
export const isSupabaseConfigured = () => Boolean(supabaseUrl && supabaseAnonKey);

// Get the site URL for redirects
const getSiteUrl = () => import.meta.env.PROD ? 'https://pokecardindex.com' : window.location.origin;

// Create and export the Supabase client
export const supabase = supabaseUrl && supabaseAnonKey
  ? createClient<Database>(supabaseUrl, supabaseAnonKey, {
      realtime: {
        params: {
          eventsPerSecond: 10
        }
      },
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true
      }
    })
  : createDummyClient() as any;

type DummySupabaseClient = Pick<SupabaseClient<Database>, 'auth' | 'from' | 'storage'>;

// Create a dummy client if environment variables are not set
function createDummyClient(): DummySupabaseClient {
  const storageError = { message: 'Storage operation not available', name: 'StorageError' };
  
  return {
    auth: {
      getSession: () => Promise.resolve({ 
        data: { session: null }, 
        error: null 
      }),
      onAuthStateChange: () => {
        const subscription: Subscription = {
          id: 'dummy-subscription',
          callback: () => {},
          unsubscribe: () => {}
        };
        return { data: { subscription } };
      },
      signInWithPassword: () => Promise.resolve({ 
        data: { 
          user: null, 
          session: null,
          weakPassword: null
        }, 
        error: new AuthError('Authentication is not available') 
      }),
      signUp: () => Promise.resolve({ 
        data: { 
          user: null, 
          session: null 
        }, 
        error: new AuthError('Authentication is not available') 
      }),
      signOut: () => Promise.resolve({ error: null }),
      signInWithOAuth: () => Promise.resolve({ 
        data: { 
          provider: 'google' as Provider, 
          url: null 
        }, 
        error: new AuthError('Authentication is not available') 
      })
    } as any,
    from: (table: string) => ({
      select: () => Promise.resolve({ data: [], error: null }),
      insert: () => Promise.resolve({ data: null, error: null }),
      update: () => Promise.resolve({ data: null, error: null }),
      delete: () => Promise.resolve({ data: null, error: null }),
      upsert: () => Promise.resolve({ data: null, error: null }),
      eq: () => ({})
    }) as any,
    storage: {
      from: (id: string) => ({
        upload: () => Promise.resolve({ data: null, error: storageError }),
        getPublicUrl: () => ({ data: { publicUrl: '' } }),
        download: () => Promise.resolve({ data: null, error: storageError }),
        list: () => Promise.resolve({ data: [], error: storageError }),
        remove: () => Promise.resolve({ data: null, error: storageError }),
        createSignedUrl: () => Promise.resolve({ data: null, error: storageError }),
        uploadOrUpdate: () => Promise.resolve({ data: null, error: storageError }),
        move: () => Promise.resolve({ data: null, error: storageError }),
        copy: () => Promise.resolve({ data: null, error: storageError }),
        url: '',
        headers: {},
        fetch: () => Promise.resolve(new Response())
      }),
      listBuckets: () => Promise.resolve({ data: [], error: storageError }),
      getBucket: () => Promise.resolve({ data: null, error: storageError }),
      createBucket: () => Promise.resolve({ data: { name: 'dummy-bucket' }, error: null }),
      updateBucket: () => Promise.resolve({ data: { name: 'dummy-bucket' }, error: null }),
      deleteBucket: () => Promise.resolve({ data: { name: 'dummy-bucket' }, error: null }),
      emptyBucket: () => Promise.resolve({ data: { name: 'dummy-bucket' }, error: null })
    }
  };
}

export interface ProfileStore {
  profile: Database['public']['Tables']['profiles']['Row'] | null;
  setProfile: (profile: Database['public']['Tables']['profiles']['Row'] | null) => void;
  isAuthenticated: boolean;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  isInitialized: boolean;
  setIsInitialized: (isInitialized: boolean) => void;
  reset: () => void;
}

const useProfileStore = create<ProfileStore>((set) => ({
  profile: null,
  setProfile: (profile) => set({ profile }),
  isAuthenticated: false,
  setIsAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
  isInitialized: false,
  setIsInitialized: (isInitialized) => set({ isInitialized }),
  reset: () => set({
    profile: null,
    isAuthenticated: false,
    isInitialized: false
  })
}));

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  setUser: (user: User | null) => void;
  setLoading: (isLoading: boolean) => void;
  clearUser: () => void;
}

// Create and export auth store
export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  setUser: (user: User | null) => set({ user, isAuthenticated: !!user }),
  setLoading: (isLoading: boolean) => set({ isLoading }),
  clearUser: () => set({ user: null, isAuthenticated: false })
}));

// Initialize auth state
let isInitialized = false;

// Initialize auth state and set up listeners
export async function initializeAuth() {
  if (isInitialized) return;
  isInitialized = true;

  // Set up auth state change listener
  supabase.auth.onAuthStateChange((event: AuthChangeEvent, session: Session | null) => {
    console.log('Auth state changed:', { event, session });
    const store = useAuthStore.getState();
    store.setUser(session?.user || null);
    store.setLoading(false);

    // Update profile store authentication state
    const profileStore = useProfileStore.getState();
    profileStore.setIsAuthenticated(!!session?.user);

    // Update collection store authentication state
    const collectionStore = useCollectionStore.getState();
    if ('setIsAuthenticated' in collectionStore) {
      (collectionStore as any).setIsAuthenticated(!!session?.user);
    }
  });

  try {
    const { data: { session } } = await supabase.auth.getSession();
    const store = useAuthStore.getState();
    store.setUser(session?.user || null);
    store.setLoading(false);

    // Update profile store authentication state
    const profileStore = useProfileStore.getState();
    profileStore.setIsAuthenticated(!!session?.user);
  } catch (error) {
    console.error('Error initializing auth:', error);
    const store = useAuthStore.getState();
    store.setLoading(false);
  }
}

// Helper function for sleep
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Export useProfileStore only once at the end
export { useProfileStore };